/**
 * Apresentação compacta da mobilidade oficial — lógica pura.
 *
 * Substitui a duplicação visual (cartão "Bilhete recomendado" + cartão grande
 * "Mobilidade oficial em …") por UMA única peça:
 *  - Free/Premium: teaser com cadeado e CTA para o Routify Pro (nunca uma
 *    pesquisa Google, nunca links oficiais desbloqueados).
 *  - Pro: acção directa para o site oficial e painel compacto com os restantes
 *    recursos relevantes ao transporte escolhido.
 *
 * Os avisos essenciais (ZTL, ULEZ, portagens, estacionamento…) são sempre
 * visíveis, em qualquer plano.
 */
import { MODE_LABEL, type MobilityMode, type MobilityResource } from "./directory";

export type MobilityGroup = {
  mode: MobilityMode | string;
  label: string;
  warning?: string;
  items: Array<Pick<MobilityResource, "id" | "providerName" | "url" | "official" | "notes"> & {
    mode?: MobilityMode | string;
  }>;
};

export type MobilityTeaserAction =
  | { kind: "upgrade"; label: string }
  | { kind: "official"; label: string; url: string; providerName: string }
  | { kind: "none"; label: string };

export type MobilityTeaser = {
  locked: boolean;
  /** Avisos essenciais — sempre visíveis, em todos os planos. */
  warnings: string[];
  /** Acção única e compacta do cartão. */
  action: MobilityTeaserAction;
  /** Recursos adicionais (apenas Pro) para o painel expansível. */
  panel: Array<{ mode: string; label: string; items: MobilityGroup["items"] }>;
  /** Total de recursos oficiais disponíveis para esta cidade/transporte. */
  resourceCount: number;
  /** Verdadeiro quando não há qualquer recurso curado para a cidade. */
  empty: boolean;
};

/** Ordem de preferência do recurso "principal" mostrado no cartão. */
const PRIMARY_ORDER: MobilityMode[] = [
  "transit_pass",
  "transit_tickets",
  "parking",
  "tolls",
  "low_emission_zone",
  "bike_share",
  "scooter",
  "car_rental",
  "ferry",
  "transfer",
  "taxi_rideshare",
];

type Lang = string;

const COPY: Record<string, { upgrade: string; buy: string; open: string; none: string; more: string; title: string }> = {
  pt: {
    upgrade: "Ver passes e links oficiais com Pro",
    buy: "Comprar no site oficial",
    open: "Ver no site oficial",
    none: "Sem fonte oficial curada para esta cidade",
    more: "Mais recursos oficiais",
    title: "Mobilidade oficial",
  },
  en: {
    upgrade: "See passes and official links with Pro",
    buy: "Buy on the official site",
    open: "Open the official site",
    none: "No curated official source for this city",
    more: "More official resources",
    title: "Official mobility",
  },
  es: {
    upgrade: "Ver pases y enlaces oficiales con Pro",
    buy: "Comprar en el sitio oficial",
    open: "Ver en el sitio oficial",
    none: "Sin fuente oficial curada para esta ciudad",
    more: "Más recursos oficiales",
    title: "Movilidad oficial",
  },
  fr: {
    upgrade: "Voir les pass et liens officiels avec Pro",
    buy: "Acheter sur le site officiel",
    open: "Voir le site officiel",
    none: "Aucune source officielle pour cette ville",
    more: "Plus de ressources officielles",
    title: "Mobilité officielle",
  },
  de: {
    upgrade: "Tickets und offizielle Links mit Pro",
    buy: "Auf der offiziellen Seite kaufen",
    open: "Offizielle Seite öffnen",
    none: "Keine offizielle Quelle für diese Stadt",
    more: "Weitere offizielle Quellen",
    title: "Offizielle Mobilität",
  },
  ja: {
    upgrade: "Proで公式パスとリンクを見る",
    buy: "公式サイトで購入",
    open: "公式サイトを開く",
    none: "この都市の公式ソースはまだありません",
    more: "その他の公式リソース",
    title: "公式モビリティ",
  },
};

export function mobilityCopy(lang: Lang) {
  return COPY[lang] ?? COPY.en;
}

/** Modos em que faz sentido dizer "comprar" em vez de apenas "ver". */
const PURCHASABLE: ReadonlySet<string> = new Set(["transit_pass", "transit_tickets", "ferry", "car_rental"]);

export function buildMobilityTeaser(input: {
  groups: MobilityGroup[];
  locked: boolean;
  lang?: Lang;
}): MobilityTeaser {
  const copy = mobilityCopy(input.lang ?? "pt");
  const groups = (input.groups ?? []).filter((g) => g.items.length > 0 || g.warning);
  const withItems = groups.filter((g) => g.items.length > 0);
  const warnings = groups.map((g) => g.warning).filter((w): w is string => Boolean(w));
  const resourceCount = withItems.reduce((n, g) => n + g.items.length, 0);

  const primaryGroup =
    withItems
      .slice()
      .sort((a, b) => {
        const ia = PRIMARY_ORDER.indexOf(a.mode as MobilityMode);
        const ib = PRIMARY_ORDER.indexOf(b.mode as MobilityMode);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      })[0] ?? null;
  const primary = primaryGroup?.items[0] ?? null;

  let action: MobilityTeaserAction;
  if (input.locked) {
    // Nunca expõe o link oficial nem uma pesquisa: só o convite ao Pro.
    action = { kind: "upgrade", label: copy.upgrade };
  } else if (primary && primaryGroup) {
    action = {
      kind: "official",
      label: PURCHASABLE.has(String(primaryGroup.mode)) ? copy.buy : copy.open,
      url: primary.url,
      providerName: primary.providerName,
    };
  } else {
    action = { kind: "none", label: copy.none };
  }

  const panel = input.locked
    ? []
    : withItems
        .map((g) => ({
          mode: String(g.mode),
          label: MODE_LABEL[g.mode as MobilityMode] ?? g.label,
          items: g === primaryGroup ? g.items.slice(1) : g.items,
        }))
        .filter((g) => g.items.length > 0);

  return {
    locked: input.locked,
    warnings,
    action,
    panel,
    resourceCount,
    empty: resourceCount === 0,
  };
}
